/**
 * AI copy engine — port of the legacy rule-based generator (__aiRead /
 * __aiEngine / __aiRegen). Deterministic, grounded copy from the Step-1 brief:
 * no LLM call — the "AI" is a campaign/persona/place decision matrix tuned to
 * Jack's voice rules (anchor leads with the most concrete thing available —
 * a real from-price beats a place; "at" for communities, "in" for markets).
 *
 * Always exactly 3 subjects: Anchor / Urgency / Social proof.
 */

export type Campaign = "webinar" | "event" | "open" | "soon" | "last" | "offer" | "sustain";
export type Persona = "FTHB" | "AA" | "MU" | "GEN";

export interface CopyContext {
  /** division's market city (from the Step-1 division label) */
  city?: string;
  community?: string;
  aoi?: string;
  masterplan?: string;
  initiative?: string;
  /** human message label; `off` = it's a real offer */
  message?: string;
  off?: boolean;
  template?: string;
  /** template stage from the gallery (Tease/Launch/Sustain/Urgency/LastChance/Events) */
  templateStage?: string;
  project?: string;
  /** Initiative Request Focus — sets granularity (Market/Division = broad "in [city]") */
  focus?: string;
  /** Step-3 Buyer Profile (FTHB | MoveUp | ActiveAdult) */
  buyerProfile?: string;
  /** live community content — real from-price beats generic phrasing */
  fromPrice?: number;
  /** duplicate-job context (subject/project feed the campaign classifier) */
  dupText?: string;
}

interface Read {
  camp: Campaign;
  persona: Persona;
  city: string;
  comm: string;
  aoi: string;
  mp: string;
  message: string;
  off: boolean;
  focus: string;
  cityOnly: boolean;
  fromPrice: number;
}

const cap = (s: string) => { s = (s || "").trim(); return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; };
const pick = (a: string[]) => a[Math.floor(Math.random() * a.length)];

/** classify the brief → campaign + persona + granularity (legacy __aiRead, DOM-decoupled) */
export function readContext(c: CopyContext): Read {
  const tpl = c.template || "", message = c.message || "";
  const blob = ((c.initiative || "") + " " + tpl + " " + message + " " + (c.project || "") + " " + (c.dupText || "")).toLowerCase();
  const stageMap: Record<string, Campaign> = { Tease: "soon", Launch: "open", Sustain: "sustain", LastChance: "last", Urgency: "last", Events: "event" };
  const tplLow = (tpl + " " + message).toLowerCase();
  const tplCamp: Campaign | "" = /webinar/.test(tplLow)
    ? "webinar"
    : stageMap[c.templateStage || ""] ||
      (/tease|coming soon|sneak|vip/.test(tplLow) ? "soon"
        : /launch|grand opening|now selling/.test(tplLow) ? "open"
        : /last chance|final|closeout|models now/.test(tplLow) ? "last"
        : /ribbon|celebration|\bevent\b/.test(tplLow) ? "event"
        : /sustain|tour your way|own your dream|homeready/.test(tplLow) ? "sustain"
        : "");
  const ip = (String(c.initiative || "").match(/^\s*\d+\s*[-–—]\s*(.+?)\s+for\s+/i) || [])[1] || "";
  let camp: Campaign =
    tplCamp ||
    (/last chance/i.test(ip) ? "last"
      : /coming soon/i.test(ip) ? "soon"
      : /grand opening|now selling|launch/i.test(ip) ? "open"
      : /sales event|nse|savings|flex/i.test(ip) ? "offer"
      : /last chance|final|closeout|last home|closing out/.test(blob) ? "last"
      : /coming soon|tease|vip|sneak|pre-?model|first look/.test(blob) ? "soon"
      : /grand opening|now open|now selling|launch/.test(blob) ? "open"
      : /flex cash|% down|down payment|closing cost|smartbuy|savings|dream savings|sale|nse|incentive|blowout|super sale/.test(blob) ? "offer"
      : "sustain");
  if (c.off && camp === "sustain") camp = "offer"; // an offer on a generic Sustain template IS an offer email
  let persona: Persona | "" = ({ FTHB: "FTHB", MoveUp: "MU", ActiveAdult: "AA" } as Record<string, Persona>)[c.buyerProfile || ""] || "";
  if (!persona) persona = /fthb|first[- ]?time/.test(blob) ? "FTHB" : /active adult|55\+|55 plus/.test(blob) ? "AA" : /move[- ]?up|next gen|move up/.test(blob) ? "MU" : "GEN";
  const comm = /market-level/i.test(c.community || "") ? "" : c.community || "";
  const focus = c.focus || "";
  const cityOnly =
    focus === "Market" || focus === "Division" ? true
      : focus === "Community" ? !comm
      : focus === "Masterplan" ? !(c.masterplan || comm)
      : focus === "Area of Interest" ? !c.aoi
      : !comm && !c.aoi && !c.masterplan;
  return { camp, persona, city: c.city || "", comm, aoi: c.aoi || "", mp: c.masterplan || "", message, off: !!c.off, focus, cityOnly, fromPrice: c.fromPrice || 0 };
}

function place(d: Read): string {
  if (d.focus === "Market" || d.focus === "Division") return d.city || d.aoi || "your area";
  if (d.focus === "Area of Interest") return d.aoi || d.comm || d.city || "your area";
  if (d.focus === "Masterplan") return d.mp || d.comm || d.aoi || d.city || "your area";
  if (d.focus === "Community") return d.comm || d.aoi || d.mp || d.city || "your area";
  return d.comm || d.aoi || d.mp || d.city || "your area";
}
const AT = (d: Read) => (d.cityOnly ? "in" : "at"); // communities get "at", a market/city gets "in"
const offMsg = (d: Read) => (d.off ? d.message : "");
const ccPrice = (d: Read) => (d.fromPrice ? "$" + Number(d.fromPrice).toLocaleString() : "");
const hasPlace = (d: Read) => { const p = place(d); return p && p !== "your area" ? p : ""; };

/* webinar + event pools (verbatim) */
const wSubjA = (d: Read) => { const o = offMsg(d), p = hasPlace(d); return o ? [p ? cap(o) + " in " + p : cap(o) + " — see how it works", "See what " + o.toLowerCase() + " could mean for you", "Your first home, with " + o.toLowerCase()] : [p ? "You could put 0% down in " + p : "You could put 0% down on your first home", "Renting? Owning may be closer than you think", p ? "New homes in " + p : "See what a monthly payment could look like"]; };
const wSubjU = (d: Read) => { const p = hasPlace(d); return [p ? "Homebuying in " + p + ", made simple" : "Your homebuying questions, answered on demand", "Watch the on-demand session in 30 minutes", "Take 30 minutes, change your timeline"]; };
const wSubjS = (d: Read) => { const p = hasPlace(d); return [p ? "See why " + p + " buyers start here" : "See why first-time buyers start here", "Thousands have watched — see what's possible", "Buyers like you are making the move"]; };
const wPre = (d: Read) => { const o = offMsg(d), p = hasPlace(d); return [o ? "Ask about " + o + (p ? " in " + p : "") + " — watch the on-demand webinar." : p ? "See what a monthly payment looks like in " + p + " — watch the on-demand session." : "See what a monthly payment looks like — watch the on-demand session.", "Our pros break down what it really takes to go from renting to owning" + (p ? " in " + p : "") + ".", "New homes" + (p ? " in " + p : "") + " — watch anytime, no sign-up."]; };
const evSubjA = (d: Read) => { const p = place(d); return ["You're invited: " + p, "Celebrate the opening of " + p, "Join us at " + p]; };
const evSubjU = (d: Read) => { const p = place(d); return ["This weekend " + AT(d) + " " + p, "Don't miss the celebration at " + p, "One weekend only " + AT(d) + " " + p]; };
const evSubjS = (d: Read) => { const p = place(d); return ["The neighborhood is celebrating " + p, "Everyone's coming to " + p]; };
const evPre = (d: Read) => { const p = place(d); return ["Join us to celebrate " + p + " — tours, food, and more.", "Be there for the ribbon cutting at " + p + ".", "Come see what's new at " + p + "."]; };

export function subjAnchor(d: Read): string {
  const p = place(d), o = offMsg(d), pr = ccPrice(d);
  if (d.camp === "webinar") return pick(wSubjA(d));
  if (d.camp === "event") return pick(evSubjA(d));
  if (pr && !d.cityOnly && d.camp !== "soon") return pick(["New homes from " + pr + " at " + p, "From " + pr + " at " + p, p + ": homes from " + pr]);
  if (d.cityOnly)
    return pick(({
      open: ["Now selling in " + p, "New homes are open in " + p],
      soon: ["New homes are coming to " + p, "Coming soon in " + p, "First look: new homes in " + p],
      last: ["Final homes in " + p, "Last homes available in " + p],
      offer: [o ? cap(o) + " in " + p : "New savings in " + p, "See what's available in " + p],
      sustain: ["See what's available in " + p, "New homes in " + p],
    } as Record<string, string[]>)[d.camp] || ["See what's available in " + p]);
  return pick(({
    open: ["Now selling: " + p, p + " is now open", "New homes at " + p],
    soon: [p + " is coming soon", "First look: " + p, "Coming soon: " + p],
    last: ["Final homes at " + p, "Closing out " + p, "The last homes at " + p],
    offer: [o ? cap(o) + " at " + p : "New savings at " + p, p + (o ? ": " + o : ": new offers"), "See what's available at " + p],
    sustain: ["See what's available at " + p, "New floor plans at " + p, p + " homes are ready to tour"],
  } as Record<string, string[]>)[d.camp] || ["See what's available at " + p]);
}

export function subjUrg(d: Read): string {
  const p = place(d), o = offMsg(d), at = AT(d);
  if (d.camp === "webinar") return pick(wSubjU(d));
  if (d.camp === "event") return pick(evSubjU(d));
  if (d.persona === "AA") return pick(["Now touring " + at + " " + p, "Homes available now " + at + " " + p, "Move-in ready homes " + at + " " + p]);
  return pick(({
    open: ["Homes are moving " + at + " " + p, "Early homesites " + at + " " + p + " won't last", "New homes " + at + " " + p + ", selling now"],
    soon: ["Get on the list for " + p, d.cityOnly ? "Be first to new homes in " + p : "Be first at " + p, d.cityOnly ? "Early access in " + p : "VIP access to " + p],
    last: ["Only a few homes left " + at + " " + p, "Last chance " + at + " " + p, "Final homesites " + at + " " + p],
    offer: [o ? cap(o) + ", for a limited time" : "Limited-time savings " + at + " " + p, "A few homes left " + at + " " + p, o ? "Ask about " + o + " before it ends" : "Homes " + at + " " + p + " going fast"],
    sustain: ["A few move-in ready homes " + at + " " + p, "Available now " + at + " " + p, "Move-in ready " + at + " " + p],
  } as Record<string, string[]>)[d.camp] || ["Available now " + at + " " + p]);
}

export function subjSoc(d: Read): string {
  const p = place(d), c = d.city && d.city !== p ? d.city : "";
  if (d.camp === "webinar") return pick(wSubjS(d));
  if (d.camp === "event") return pick(evSubjS(d));
  return pick(({
    open: ["See why buyers chose " + p, p + " is drawing a crowd", c ? p + " is one of " + c + "'s new favorites" : p + " is filling up"],
    soon: ["Buyers are lining up for " + p, (c ? c + " buyers" : "Buyers") + " are watching " + p],
    last: ["Buyers claimed most of " + p, "Almost sold out: " + p],
    offer: ["See why " + p + " is selling", "Buyers are acting fast " + AT(d) + " " + p],
    sustain: ["Families are settling into " + p, c ? p + " is one of " + c + "'s favorites" : "Buyers keep choosing " + p],
  } as Record<string, string[]>)[d.camp] || ["See why buyers choose " + p]);
}

export function genPre(d: Read): string {
  const p = place(d), o = offMsg(d), at = AT(d), pr = ccPrice(d);
  if (d.camp === "webinar") return pick(wPre(d));
  if (d.camp === "event") return pick(evPre(d));
  if (o) return pick(["Ask about " + o + " while homes last " + at + " " + p + ".", cap(o) + " " + at + " " + p + " — see what's available.", "New homes " + at + " " + p + (pr ? " from " + pr : "") + ", plus " + o.toLowerCase() + "."]);
  return pick([
    pr ? "New homes " + at + " " + p + " from " + pr + "." : "See floor plans, pricing, and what's available " + at + " " + p + ".",
    d.cityOnly ? "See what's new in " + p + " this week." : "Book a tour of " + p + " this week.",
    "Homes built for how you actually live, in " + p + ".",
  ]);
}

export function genHead(d: Read): string {
  const p = place(d), o = offMsg(d);
  return pick(({
    webinar: ["Owning May Be Closer Than You Think", "From Renting to Owning", "Your Path to a First Home"],
    event: [d.cityOnly ? "You're Invited" : "You're Invited to " + p, "Celebrate " + p],
    open: ["Now Open in " + p, d.cityOnly ? "New Homes, Now Selling" : p + " Is Here", "New Homes, Now Selling"],
    soon: [d.cityOnly ? "New Homes Are Coming to " + p : p + " Is Almost Here", "Coming Soon" + (d.cityOnly ? " to " + p : ": " + p), d.cityOnly ? "Be First to What's Next" : "Be First at " + p],
    last: ["The Last Homes " + AT(d) + " " + cap(p), "Final Homesites " + AT(d) + " " + cap(p)],
    offer: [o ? cap(o) : "New Ways to Save", "Own for Less " + AT(d) + " " + p],
    sustain: ["Find Your Home " + AT(d) + " " + p, "New Homes " + AT(d) + " " + p],
  } as Record<string, string[]>)[d.camp] || ["New Homes " + AT(d) + " " + p]);
}

export function genBody(d: Read): string {
  const p = place(d), o = offMsg(d), at = AT(d), pr = ccPrice(d);
  if (d.camp === "webinar")
    return "Rent doesn't have to be forever. Watch our on-demand homebuyer webinar — our pros break down what it really takes to go from renting to owning" + (o ? ", including " + o.toLowerCase() : "") + ". With Lennar Mortgage, see what's possible based on your goals.";
  if (d.camp === "event") return "Join us to celebrate " + p + ". Tour the homes, meet the team, and see what's new" + (o ? " — plus " + o.toLowerCase() : "") + ".";
  const pOpen = ({
    FTHB: "Rent doesn't have to be forever. New homes " + at + " " + p + (pr ? " start at " + pr : " are ready to tour") + ", with everything included at one clear price.",
    AA: "A low-maintenance home " + at + " " + p + " is ready when you are" + (pr ? ", from " + pr : "") + ".",
    MU: "More space, newer everything. Homes " + at + " " + p + (pr ? " start at " + pr : " are available now") + ".",
    GEN: "New homes are available " + at + " " + p + (pr ? ", from " + pr : "") + ".",
  } as Record<string, string>)[d.persona];
  const mid = o ? " " + cap(o) + "." : "";
  const close = ({
    open: " See the floor plans and book a tour before the first homesites go.",
    soon: " Join the interest list for pricing and first access when homes release.",
    last: " See what's left before the final homesites are gone.",
    offer: " See what's available, then talk to a New Home Consultant.",
    sustain: " See floor plans and quick move-ins, or book a tour.",
  } as Record<string, string>)[d.camp];
  return pOpen + mid + close;
}

const cta1by = (d: Read): string[] =>
  (({
    webinar: ["Watch On-Demand", "Watch Now", "See What's Possible"],
    event: ["RSVP Now", "Save Your Spot", "Join Us"],
    open: ["See Available Homes", "Explore Homes", "Schedule a Tour"],
    soon: ["Join the Interest List", "Get Early Access", "Request Pricing"],
    last: ["See Remaining Homes", "Tour Before They're Gone", "View Available Homes"],
    offer: ["See Homes & Offers", "Explore Homes", "View Available Homes"],
    sustain: ["Explore Homes", "Schedule a Tour", "View Floor Plans"],
  } as Record<string, string[]>)[d.camp] || ["Explore Homes"]);

const eyeby = (d: Read): string =>
  (({ webinar: "ON-DEMAND WEBINAR", event: "YOU'RE INVITED", open: "NOW OPEN", soon: "COMING SOON", last: "LAST CHANCE", offer: "LIMITED-TIME", sustain: "NEW HOMES" } as Record<string, string>)[d.camp] || "NEW HOMES");

export interface CopyDraft {
  subjects: { k: "Anchor" | "Urgency" | "Social proof"; text: string }[];
  preheader: string;
  headline: string;
  body: string;
  cta1: string;
  cta2: string;
  eyebrow: string;
}

/** legacy __aiEngine — one complete draft */
export function draftCopy(ctx: CopyContext): CopyDraft {
  const d = readContext(ctx);
  return {
    subjects: [
      { k: "Anchor", text: subjAnchor(d) },
      { k: "Urgency", text: subjUrg(d) },
      { k: "Social proof", text: subjSoc(d) },
    ],
    preheader: genPre(d),
    headline: genHead(d),
    body: genBody(d),
    cta1: pick(cta1by(d)),
    cta2: "Talk to a New Home Consultant",
    eyebrow: eyeby(d),
  };
}

/** readout chips for the AI copy studio header (legacy __aiCopyStudio draw()) */
export interface CopyChip { text: string; gold?: boolean }
export function contextChips(ctx: CopyContext): CopyChip[] {
  const r = readContext(ctx);
  /* verbatim monolith maps — webinar/event intentionally missing (renders "undefined"), do not fix here */
  const campLbl = ({ open: "Grand Opening", soon: "Coming Soon", last: "Last Chance", offer: "Offer", sustain: "Sustain" } as Record<string, string>)[r.camp];
  const perLbl = ({ FTHB: "First-time", AA: "Active Adult", MU: "Move-Up", GEN: "General" } as Record<string, string>)[r.persona];
  const chips: CopyChip[] = [
    { text: "Campaign · " + campLbl },
    { text: "Persona · " + perLbl },
    { text: (r.comm ? "Community · " : r.mp ? "Masterplan · " : r.aoi ? "Area · " : "Market · ") + place(r) },
  ];
  const pr = ccPrice(r);
  if (pr) chips.push({ text: "From " + pr + " · live pricing", gold: true });
  return chips;
}

/** legacy __aiRegen — regenerate a single field */
export function regenField(ctx: CopyContext, field: "preheader" | "headline" | "body" | "cta1" | "Anchor" | "Urgency" | "Social proof"): string {
  const d = readContext(ctx);
  switch (field) {
    case "preheader": return genPre(d);
    case "headline": return genHead(d);
    case "body": return genBody(d);
    case "cta1": return pick(cta1by(d));
    case "Anchor": return subjAnchor(d);
    case "Urgency": return subjUrg(d);
    case "Social proof": return subjSoc(d);
  }
}
